import React, { useEffect, useState, useCallback } from "react";
import { api, formatDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "./Dashboard";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { UserPlus, Pencil, Trash2, ShieldCheck, ShieldAlert } from "lucide-react";

const ROLE_META = {
  admin: { label: "Admin", className: "border-violet-200 bg-violet-50 text-violet-700" },
  member: { label: "Member", className: "border-sky-200 bg-sky-50 text-sky-700" },
  viewer: { label: "Viewer", className: "border-slate-200 bg-slate-100 text-slate-600" },
};

const RoleBadge = ({ role }) => {
  const m = ROLE_META[role] || ROLE_META.viewer;
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${m.className}`}>{m.label}</span>;
};

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin — full access + manage users" },
  { value: "member", label: "Member — full access, no user management" },
  { value: "viewer", label: "Viewer — read-only" },
];

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.listUsers();
      setUsers(r.data);
    } catch (e) {
      toast.error("Could not load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doDelete = async () => {
    try {
      await api.deleteUser(deleteTarget.id);
      toast.success("User removed");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete user");
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        subtitle="Manage who can access TollBill and what they can do"
        right={
          <Button className="gap-2" data-testid="users-add-button" onClick={() => setCreateOpen(true)}>
            <UserPlus className="h-4 w-4" /> Add user
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-3">{["s1", "s2", "s3", "s4"].map((k) => <Skeleton key={k} className="h-12 w-full" />)}</div>
          ) : (
            <Table data-testid="users-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last login</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid="users-row">
                    <TableCell className="font-medium">
                      {u.name || "—"}{u.id === me?.id && <span className="ml-2 text-xs text-muted-foreground">(you)</span>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                    <TableCell><RoleBadge role={u.role} /></TableCell>
                    <TableCell>
                      {u.active ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" /> Active</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground"><ShieldAlert className="h-3.5 w-3.5" /> Disabled</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.last_login ? formatDate(u.last_login) : "Never"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <Button size="icon" variant="ghost" data-testid="users-edit-button" onClick={() => setEditTarget(u)} aria-label="Edit user">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" data-testid="users-delete-button" disabled={u.id === me?.id} onClick={() => setDeleteTarget(u)} aria-label="Delete user">
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <UserFormDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={load} />
      <UserFormDialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)} user={editTarget} onSaved={load} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="users-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this user?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.email} will lose access immediately. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="users-delete-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const UserFormDialog = ({ open, onOpenChange, user, onSaved }) => {
  const editing = !!user;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [active, setActive] = useState(true);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(user?.name || "");
    setEmail(user?.email || "");
    setRole(user?.role || "viewer");
    setActive(user?.active ?? true);
    setPassword("");
  }, [open, user]);

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const payload = { name, role, active };
        if (password) payload.password = password;
        await api.updateUser(user.id, payload);
        toast.success("User updated");
      } else {
        await api.createUser({ email: email.trim(), name, role, password });
        toast.success("User created");
      }
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="user-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit user" : "Add user"}</DialogTitle>
          <DialogDescription>
            {editing ? "Update this user's details, role, or access." : "Create a new account and assign a role."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="uf-email">Email</Label>
            <Input id="uf-email" data-testid="user-form-email" type="email" value={email} disabled={editing}
              placeholder="user@company.com" onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="uf-name">Name</Label>
            <Input id="uf-name" data-testid="user-form-name" value={name} placeholder="Full name" onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="uf-password">{editing ? "Reset password (optional)" : "Temporary password"}</Label>
            <Input id="uf-password" data-testid="user-form-password" type="text" value={password}
              placeholder={editing ? "Leave blank to keep current" : "At least 6 characters"} onChange={(e) => setPassword(e.target.value)} />
          </div>
          {editing && (
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <div>
                <div className="text-sm font-medium">Account active</div>
                <div className="text-xs text-muted-foreground">Disabled users can't sign in.</div>
              </div>
              <Switch checked={active} onCheckedChange={setActive} data-testid="user-form-active" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="user-form-save">{saving ? "Saving..." : (editing ? "Save changes" : "Create user")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
